<template>
  <div>
    <v-dialog
      :value="value"
      @input="emitDialogState"
      max-width="800px"
      :fullscreen="isMobile"
    >
      <v-card class="d-flex flex-column">
        <v-card-title
          style="
            display: flex;
            justify-content: space-between;
            flex-wrap: nowrap;
          "
          :style="{
            ...(isMobile ? { padding: '20px 16px 12px 16px' } : {}),
          }"
        >
          <div
            :style="{
              fontSize: isMobile ? '16px' : '20px',
            }"
          >
            Eigenes Feld bearbeiten
          </div>
          <v-icon @click="closeDialog">{{ mdiClose }}</v-icon>
        </v-card-title>
        <v-card-text
          style="padding-bottom: 4px"
          :style="{
            ...(isMobile ? { padding: '16px' } : {}),
          }"
        >
          <CustomAttributesForm
            ref="customAttributesForm"
            @commit="handleUpdate($event)"
            @dirty="setEnableSave"
            :initial-value="customAttribute"
          />
          <v-row
            v-if="updateErrors.length > 0"
            ref="updateErrorsContainer"
            style="margin-top: 8px"
            no-gutters
          >
            <CustomAttributesUpdateErrors :update-errors="updateErrors" />
          </v-row>
          <ErrorText style="margin-bottom: 8px" v-if="serverError">
            {{ serverError }}
          </ErrorText>
        </v-card-text>
        <v-spacer></v-spacer>
        <v-card-actions style="padding-bottom: 16px">
          <v-spacer></v-spacer>
          <TextButton
            @click="cancelForm"
            style="margin-right: 8px"
            label="Abbrechen"
          ></TextButton>
          <Button
            :loading="loading"
            :disabled="!enableSave || loading"
            @click="submitForm"
            >Speichern</Button
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, PropType, Ref, ref } from "vue"
import CustomAttributesUpdateErrors from "app/modules/custom-attributes/CustomAttributesUpdateErrors.vue"
import Button from "components/UI/Button.vue"
import CustomAttributesForm from "app/modules/custom-attributes/form/CustomAttributesForm.vue"
import { mdiClose } from "@mdi/js"
import {
  CustomAttributeDefinitionUpdateError,
  CustomAttributesClient,
  LockedException,
} from "app/modules/custom-attributes/client"
import { CustomAttributeDefinition } from "suus-api"
import TextButton from "components/UI/TextButton.vue"
import { useIsMobile } from "features/common/vuetify/useVuetify"
import SuccessSnackbar from "app/ui-primitives/snackbars/SuccessSnackbar.vue"
import ErrorText from "app/ui-primitives/text/ErrorText.vue"
import { useSnackbar } from "app/ui-primitives/snackbars/SnackbarProvider.vue"

export default defineComponent({
  name: "CustomAttributesEditDialog",
  components: {
    ErrorText,
    SuccessSnackbar,
    TextButton,
    CustomAttributesForm,
    Button,
    CustomAttributesUpdateErrors,
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    customAttribute: {
      type: Object as PropType<CustomAttributeDefinition>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const loading = ref(false)
    const serverError: Ref<string | null> = ref(null)

    const emitDialogState = (state: boolean) => {
      emit("input", state)
    }
    const closeDialog = () => {
      updateErrors.value = []
      emitDialogState(false)
    }

    const updateErrors = ref<CustomAttributeDefinitionUpdateError[]>([])
    const updateErrorsContainer = ref<HTMLElement | null>(null)

    const scrollToErrors = () => {
      if (updateErrorsContainer.value) {
        updateErrorsContainer.value.scrollIntoView({ behavior: "smooth" })
      }
    }

    const { showSuccess } = useSnackbar()

    const handleUpdate = async (customAttribute: CustomAttributeDefinition) => {
      updateErrors.value = []
      serverError.value = null
      loading.value = true

      try {
        const updatedAttributeOrUpdateErrors =
          await CustomAttributesClient.update(customAttribute)
        // If the update returns an array, it contains errors.
        if (updatedAttributeOrUpdateErrors instanceof Array) {
          updateErrors.value = updatedAttributeOrUpdateErrors
          nextTick().then(scrollToErrors)
        } else {
          customAttributesForm.value?.resetForm()
          closeDialog()
          showSuccess("Das Feld wurde erfolgreich aktualisiert.")
          emit("updated", updatedAttributeOrUpdateErrors)
        }
      } catch (error) {
        if (error instanceof LockedException) {
          emit("updated", {
            ...props.customAttribute,
            update_in_progress: true,
          })
          serverError.value =
            "Es befindet sich bereits ein Update in Bearbeitung. Versuche es später erneut."
          console.error(error)
          return
        }
        serverError.value =
          "Es gab einen Fehler beim Aktualisieren des Attributs."
        console.error(error)
      } finally {
        loading.value = false
      }
    }

    const customAttributesForm = ref<InstanceType<
      typeof CustomAttributesForm
    > | null>(null)

    const enableSave = ref(false)
    const setEnableSave = (value: boolean) => {
      enableSave.value = value
    }

    const submitForm = () => {
      customAttributesForm.value?.submitForm()
    }

    const cancelForm = () => {
      customAttributesForm.value?.resetForm()
      closeDialog()
    }

    const isMobile = useIsMobile()

    return {
      mdiClose,
      emitDialogState,
      closeDialog,
      handleUpdate,
      updateErrors,
      updateErrorsContainer,
      customAttributesForm,
      enableSave,
      setEnableSave,
      submitForm,
      cancelForm,
      isMobile,
      loading,
      serverError,
    }
  },
})
</script>
